import { Button, Tooltip } from 'antd';
import { CalendarSync, Import, Sparkles, Wand2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../../context/AuthContext';
import EpcSchedulePanel, { type EpcScheduleRevisionDetail } from '../../schedule/EpcSchedulePanel';
import { BulkTemplateMatchWizard } from '../../templates/bulkmatch/BulkTemplateMatchWizard';
import { AutomatchWizard } from '../../standards/automatch/AutomatchWizard';
import { StandardApplyWizard } from '../../standards/apply/StandardApplyWizard';
import { RollingPlanConfigModal } from '../../standards/rolling/RollingPlanConfigModal';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import { useEpcProjectDetail } from '../EpcProjectDetailContext';

type ImportHistoryRow = {
  version?: number;
  source?: string;
  originalFileName?: string;
  status?: string;
  createdAt?: string;
};

export default function EpcProjectScheduleTab() {
  const { id, project, snapshot, importHistory, canUpdate, hasSchedule, reload } =
    useEpcProjectDetail();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [applyOpen, setApplyOpen] = useState(false);
  const [automatchOpen, setAutomatchOpen] = useState(false);
  const [rollingOpen, setRollingOpen] = useState(false);
  const [bulkMatchOpen, setBulkMatchOpen] = useState(false);

  const canApplyStandard = hasPermission('epcStandardSchedule', 'apply');
  const canConfigureRolling = hasPermission('epcProject', 'update');
  const canTemplateMap = hasPermission('epcProject', 'templateMap');

  const currentVersion = useMemo(() => {
    let max = 0;
    for (const row of importHistory) {
      const v = Number((row as { version?: unknown })?.version);
      if (Number.isFinite(v) && v > max) max = v;
    }
    return max;
  }, [importHistory]);
  /* Latest ready import — the revision the grid is currently showing. */
  const currentRevision = useMemo<EpcScheduleRevisionDetail | null>(() => {
    let best: ImportHistoryRow | null = null;
    for (const raw of importHistory) {
      const row = (raw && typeof raw === 'object' ? raw : {}) as ImportHistoryRow;
      if (row.status !== 'ready') continue;
      const v = Number(row.version);
      if (!Number.isFinite(v)) continue;
      if (!best || v > Number(best.version)) best = row;
    }
    if (!best) return null;
    return {
      version: Number(best.version),
      originalFileName: best.originalFileName,
      source: best.source,
      createdAt: best.createdAt,
    };
  }, [importHistory]);
  if (!project) return null;
  const tasks = (snapshot?.tasks as EpcScheduleTaskRow[] | undefined) || [];

  /** Wizard is closed and its result routed. */
  const onStaged = (stagedImportId: string) => {
    setApplyOpen(false);
    navigate(`/epc/projects/${id}/schedule/compare/${stagedImportId}`);
  };
  const onApplied = () => {
    setApplyOpen(false);
    // The wizard's commit mutation already invalidates the TanStack Query keys
    // (day-plan / workspace / epc-project / apply-runs) and toasts. This tab's
    // snapshot + import history are context-fetched (not query-cached), so
    // reload() is what refreshes the newly materialised revision here.
    void reload();
    void queryClient.invalidateQueries({ queryKey: ['epc-project', id] });
    void queryClient.invalidateQueries({ queryKey: ['epc-dm', 'day-plan', id] });
  };

  return (
    <div className="space-y-4">
      {/* {(canApplyStandard || canTemplateMap) && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canApplyStandard && canConfigureRolling && (
            <Button
              icon={<CalendarSync size={15} aria-hidden />}
              onClick={() => setRollingOpen(true)}
              style={{ borderColor: SOVEREIGN.btnBorder, color: SOVEREIGN.bottle }}
            >
              Rolling plan…
            </Button>
          )}
          {canApplyStandard && (
            <Tooltip
              title={currentRevision ? undefined : 'Import or apply a schedule first'}
            >
              <Button
                icon={<Wand2 size={15} aria-hidden />}
                disabled={!currentRevision}
                onClick={() => setAutomatchOpen(true)}
                style={{ borderColor: SOVEREIGN.btnBorder, color: SOVEREIGN.bottle }}
              >
                Auto-match templates
              </Button>
            </Tooltip>
          )}
          {canTemplateMap && (
            <Tooltip title={currentRevision ? undefined : 'Import a schedule first'}>
              <Button
                icon={<Wand2 size={15} aria-hidden />}
                disabled={!currentRevision}
                onClick={() => setBulkMatchOpen(true)}
                style={{ borderColor: SOVEREIGN.btnBorder, color: SOVEREIGN.bottle }}
              >
                Auto-map templates (library)
              </Button>
            </Tooltip>
          )}
          {canApplyStandard && (
            <Button
              type="primary"
              icon={<Sparkles size={15} aria-hidden />}
              onClick={() => setApplyOpen(true)}
              style={{ background: SOVEREIGN.btn, borderColor: SOVEREIGN.btnBorder }}
            >
              Apply standard schedule
            </Button>
          )}
        </div>
      )} */}

      {/* Empty-state: no snapshot yet — apply a standard or import an .mpp. */}
      {canApplyStandard && !hasSchedule && (
        <section
          className="flex flex-col gap-3 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between"
          style={{
            background: SOVEREIGN.cardBg,
            border: `1.5px solid ${SOVEREIGN.cardBorder}`,
          }}
        >
          <div className="flex items-start gap-3">
            <span
              className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl"
              style={{ background: SOVEREIGN.paper, color: SOVEREIGN.brand }}
            >
              <Sparkles size={18} aria-hidden />
            </span>
            <div>
              <div className="text-[14px] font-bold" style={{ color: SOVEREIGN.title }}>
                No schedule yet
              </div>
              <div className="mt-0.5 text-[12.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
                Apply your category&rsquo;s standard schedule, or import an{' '}
                <code className="rounded bg-[#fcfaf3] px-1 text-[11px]">.mpp</code>.
              </div>
            </div>
          </div>
          <div className="flex flex-none flex-wrap items-center gap-2">
            <Button
              type="primary"
              icon={<Sparkles size={15} aria-hidden />}
              onClick={() => setApplyOpen(true)}
              style={{ background: SOVEREIGN.btn, borderColor: SOVEREIGN.btnBorder }}
            >
              Apply standard schedule
            </Button>
            <Button
              icon={<Import size={15} aria-hidden />}
              onClick={() => navigate(`/epc/projects/${id}/mpp`)}
              style={{ borderColor: SOVEREIGN.btnBorder, color: SOVEREIGN.bottle }}
            >
              Import .mpp
            </Button>
          </div>
        </section>
      )}

      <EpcSchedulePanel
        projectId={id}
        tasks={tasks}
        onScheduleChanged={reload}
        fullSnapshot={snapshot}
        projectCode={project.code}
        canUpdate={canUpdate}
        currentVersion={currentVersion}
        currentRevision={currentRevision}
      />

      {canApplyStandard && (
        <StandardApplyWizard
          open={applyOpen}
          projectId={id}
          onClose={() => setApplyOpen(false)}
          onStaged={onStaged}
          onApplied={onApplied}
        />
      )}

      {canApplyStandard && (
        <AutomatchWizard
          open={automatchOpen}
          projectId={id}
          onClose={() => setAutomatchOpen(false)}
          onDone={onApplied}
        />
      )}

      {canTemplateMap && (
        <BulkTemplateMatchWizard
          open={bulkMatchOpen}
          projectId={id}
          onClose={() => setBulkMatchOpen(false)}
          onDone={() => {
            setBulkMatchOpen(false);
            // The commit hook already invalidates mapping/day-plan/project keys;
            // reload() refreshes the context-fetched snapshot + import history.
            void reload();
          }}
        />
      )}

      {canConfigureRolling && (
        <RollingPlanConfigModal
          open={rollingOpen}
          projectId={id}
          onClose={() => setRollingOpen(false)}
        />
      )}
    </div>
  );
}
